import React, { useRef } from 'react';
import { Trash2, Search, X } from 'lucide-react';
import {
  Combobox,
  ComboboxInput,
  ComboboxOptions,
  ComboboxOption,
  ComboboxButton,
} from '@headlessui/react';
import { Task } from 'types/project/tasks';
import { workTypeUIMap } from '../Dropdowns/TaskTypeDropdown';

interface ChildItemsLinkComboboxProps {
  inputValue: string;
  onInputChange: (val: string) => void;
  selectedTask: Task | null;
  onSelectTask: (task: Task | null) => void;
  searchResults: Task[];
  isAdding: boolean;
  disabled: boolean;
  onAdd: () => void;
  onCancel: () => void;
  showParentBadge?: boolean;
}

const ChildItemsLinkCombobox: React.FC<ChildItemsLinkComboboxProps> = ({
  inputValue,
  onInputChange,
  selectedTask,
  onSelectTask,
  searchResults,
  isAdding,
  disabled,
  onAdd,
  onCancel,
  showParentBadge = false,
}) => {
  const comboboxBtnRef = useRef<HTMLButtonElement>(null);

  const handleClearSelection = () => {
    onSelectTask(null);
    onInputChange('');
  };

  return (
    <Combobox
      as="div"
      className="flex flex-wrap items-start gap-3 w-full relative z-[50] mb-4 transition-all animate-in fade-in slide-in-from-top-2 duration-200"
      value={null}
      onChange={(task: Task | null) => {
        if (task) {
          onSelectTask(task);
          onInputChange(task.title || task.workItemTitle || '');
        }
      }}
    >
      {({ open }) => (
        <>
          {/* Search / selection area */}
          <div className="flex-1 min-w-[200px] relative">

            {/* ── LOCKED STATE: task already selected ───────────────────── */}
            {selectedTask ? (
              <div className="bg-primary-variant border border-purple-500/50 rounded-lg px-3 py-1 flex items-center min-h-[42px] gap-2 w-full">
                {(() => {
                  const category = selectedTask.workTypeCategory;
                  const iconData = category ? workTypeUIMap[category as keyof typeof workTypeUIMap] : null;
                  return (
                    <>
                      {iconData && (
                        <span className={`${iconData.color} shrink-0 text-xs`}>{iconData.icon}</span>
                      )}
                      <span className="text-xs font-semibold text-neutral-light shrink-0">
                        {selectedTask.workItemKey}
                      </span>
                      <span className="text-sm text-neutral truncate flex-1">
                        {selectedTask.title || selectedTask.workItemTitle}
                      </span>
                    </>
                  );
                })()}
                <button
                  type="button"
                  onClick={handleClearSelection}
                  disabled={disabled || isAdding}
                  className="shrink-0 p-1 rounded text-neutral-medium hover:text-white hover:bg-primary-subtle transition-colors disabled:opacity-40"
                  title="Clear selection"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (

            /* ── SEARCH STATE: no task selected yet ───────────────────── */
            <div className="bg-primary-variant border border-primary-subtle rounded-lg px-3 py-1 flex items-center min-h-[42px] relative z-20 w-full focus-within:border-purple-500/50 transition-colors">
              <Search className="w-4 h-4 text-neutral-medium mr-2 shrink-0" />
              <ComboboxInput
                autoFocus
                value={inputValue}
                onChange={e => {
                  onInputChange(e.target.value);
                }}
                disabled={disabled}
                maxLength={255}
                placeholder="Search existing work items..."
                className="flex-1 bg-transparent outline-none text-sm text-neutral placeholder:text-neutral-medium disabled:opacity-50 min-w-[120px]"
                onFocus={() => {
                  if (!open) comboboxBtnRef.current?.click();
                }}
                onClick={() => {
                  if (!open) comboboxBtnRef.current?.click();
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' && selectedTask) {
                    const activeEl = document.activeElement;
                    if (activeEl?.getAttribute('role') !== 'option') {
                      e.preventDefault();
                      onAdd();
                    }
                  }
                }}
              />
              <ComboboxButton ref={comboboxBtnRef} className="hidden" />
            </div>
            )}

            {/* Dropdown options — only shown in search state */}
            {!selectedTask && (
              <ComboboxOptions className="absolute top-[46px] left-0 w-full max-h-[250px] overflow-auto rounded-lg bg-primary-variant border border-primary-subtle shadow-xl py-1 z-[100] empty:hidden">
                {searchResults.length === 0 && inputValue.length > 0 && (
                  <div className="relative cursor-default select-none px-4 py-2 text-neutral-medium text-sm">
                    No matching existing work items found.
                  </div>
                )}
                {searchResults.map(task => {
                  const category = task.workTypeCategory;
                  const iconData = category ? workTypeUIMap[category as keyof typeof workTypeUIMap] : null;
                  const displayTitle = task.title || task.workItemTitle || 'No Title';
                  return (
                    <ComboboxOption
                      key={task.workItemUuid}
                      value={task}
                      className={({ focus }) =>
                        `relative cursor-default select-none py-2 px-4 flex items-center justify-between gap-3 ${
                          focus ? 'bg-primary-subtle text-neutral' : 'text-neutral-medium'
                        }`
                      }
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        {iconData && (
                          <span className={`${iconData.color} shrink-0`}>{iconData.icon}</span>
                        )}
                        <span className="text-xs font-semibold text-neutral-light shrink-0">
                          {task.workItemKey}
                        </span>
                        <span className="text-sm truncate">{displayTitle}</span>
                        {showParentBadge && (task as any).hasExistingParent && (task as any).parentWorkItemKey && (
                          <span className="text-[10px] bg-purple-500/20 text-purple-300 border border-purple-500/20 px-2 py-0.5 rounded font-semibold shrink-0">
                            {(task as any).parentWorkItemKey} &uarr;
                          </span>
                        )}
                      </div>
                      <span className="text-[10px] bg-primary-subtle px-2 py-0.5 rounded uppercase">
                        {category}
                      </span>
                    </ComboboxOption>
                  );
                })}
              </ComboboxOptions>
            )}
          </div>

          {/* Add button */}
          <button
            onClick={onAdd}
            disabled={disabled || !selectedTask || isAdding}
            className="bg-primary-subtle hover:bg-primary-intermediate text-neutral-light text-sm font-semibold px-5 min-h-[42px] rounded-lg transition-colors disabled:opacity-50 shrink-0 flex items-center justify-center gap-2 min-w-[80px]"
          >
            {isAdding ? (
              <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            ) : 'Add'}
          </button>

          {/* Cancel button */}
          <button
            onClick={onCancel}
            disabled={disabled}
            className="bg-primary-subtle text-neutral-medium hover:text-red-400 text-sm px-3.5 h-[42px] rounded-lg transition-colors disabled:opacity-50 shrink-0 flex items-center justify-center"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </>
      )}
    </Combobox>
  );
};

export default ChildItemsLinkCombobox;
